/**
 * StructureButton — reusable "🪄 Structurer avec l'IA" button for wizard steps.
 *
 * Calls /api/ai?route=structure, shows loading state, reports result via onResult.
 * On error, shows the error inline (but does not block the user).
 */

import { useState, useEffect } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AiStructureType } from '../../lib/ai-types';

interface StructureButtonProps {
  type: AiStructureType;
  text: string;
  context?: {
    position?: string;
    company?: string;
    experience?: Array<{ position: string; company: string; description: string }>;
  };
  onResult: (result: { description?: string; summary?: string; skills?: string[] }) => void;
  onBeforeApply?: () => void;
  onUndo?: () => void;
  disabled?: boolean;
  label?: string;
  className?: string;
}

export function StructureButton({
  type,
  text,
  context,
  onResult,
  onBeforeApply,
  onUndo,
  disabled,
  label = 'Structurer avec l\'IA',
  className,
}: StructureButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    setApplied(false);
  }, [text]);

  const handleClick = async () => {
    if (!text.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ route: 'structure', type, text, context }),
      });
      const json = await res.json() as Record<string, unknown>;
      if (!res.ok) {
        throw new Error((json.error as string) || 'Erreur serveur');
      }
      onBeforeApply?.();
      onResult(json as { description?: string; summary?: string; skills?: string[] });
      setApplied(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || loading || !text.trim()}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all',
          'bg-purple-600/20 text-purple-300 border border-purple-500/30',
          'hover:bg-purple-600/30 hover:border-purple-400/50',
          'disabled:opacity-40 disabled:cursor-not-allowed',
          className,
        )}
      >
        {loading ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <Sparkles className="size-3.5" />
        )}
        {loading ? 'Structuration en cours…' : label}
      </button>
      {applied && onUndo && (
        <button
          type="button"
          onClick={() => { onUndo(); setApplied(false); }}
          className="text-xs text-white/40 hover:text-white/60 transition-colors"
        >
          ↩ Annuler la reformulation
        </button>
      )}
      {error && (
        <p className="text-xs text-red-400">{error}</p>
      )}
    </div>
  );
}
